/**
 * SuspendDialog — suspend-a-seller-with-reason (STORY-008-007, AC#3). Clones the
 * shipped `seller-applications/RejectDialog` shape: a `useState` reason form in a
 * `BottomSheet`, confirm disabled until valid. `POST /admin/marketplace/sellers/
 * {id}/suspend` sets `verification_status='suspended'`; the backend writes the
 * audit row with the reason. Reason is **1–1000 chars** (frozen
 * `SellerSuspendRequest`).
 */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useSuspendSeller } from "@/features/admin/sellers/hooks";

export interface SuspendDialogProps {
  open: boolean;
  sellerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const MIN_REASON = 1;
const MAX_REASON = 1000;

export function SuspendDialog({
  open,
  sellerId,
  onClose,
  onSuccess,
}: SuspendDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { mutateAsync, isPending } = useSuspendSeller();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const isValid = reason.length >= MIN_REASON && reason.length <= MAX_REASON;

  const handleSuspend = useCallback(async () => {
    if (!isValid) return;
    setError(null);
    try {
      await mutateAsync({ id: sellerId, reason });
      onSuccess();
    } catch {
      setError(t("error.generic"));
    }
  }, [isValid, mutateAsync, sellerId, reason, onSuccess, t]);

  return (
    <BottomSheet open={open} onClose={onClose} title={t("sellers.suspend.title")}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="suspend-reason" className="text-sm font-medium">
            {t("sellers.suspend.reasonLabel")}
          </label>
          <textarea
            id="suspend-reason"
            data-testid="suspend-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("sellers.suspend.reasonPlaceholder")}
            rows={4}
            maxLength={MAX_REASON}
            aria-describedby="suspend-reason-hint"
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
          />
          <p id="suspend-reason-hint" className="text-xs text-[var(--g-secondary)]">
            {t("sellers.suspend.reasonHint")} ({reason.length}/{MAX_REASON})
          </p>
        </div>

        {error ? (
          <p role="alert" className="text-sm text-[var(--g-error)]">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isPending}
            className="min-h-[44px]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            data-testid="suspend-confirm-btn"
            onClick={() => void handleSuspend()}
            disabled={!isValid}
            loading={isPending}
            className="min-h-[44px]"
          >
            {t("sellers.suspend.confirm")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default SuspendDialog;
